import { productVariableScope, resolveVariables, } from "./resolveVariables.js";
// The document⇄variable translator.
//
// `evaluate` is the document-facing entry point. It owns the translation of
// `Expression`/`Property` into the pure engine's `Variable` model, then
// delegates evaluation to `resolveVariables` (`resolveVariables.ts`), which is
// document-agnostic and assumes its input is already topologically ordered.
//
// `evaluate` no longer owns a dependency graph or a topological-ordering step:
// the caller (the document-derivation phase) supplies the expressions already in
// dependency order — a producer before every expression that reads it — using
// the subset-scoped `topologicalOrder(graph, seeds)` operation. `evaluate`
// keeps that order, so a producer placed after its consumer yields the consumer
// `null` (no reordering).
// Scope used for the synthetic ref of a non-property target. Such targets
// produce a value nobody reads via `prop(...)`, so the ref only needs to be
// unique and unable to collide with a real `(itemId, propertyKey)` property.
const NON_PROPERTY_SCOPE = " non-property";
/**
 * The variable ref an expression's result is written to. An `ITEM_PROPERTY_VALUE`
 * expression produces the property `(itemId, propertyKey)`, so its ref is that
 * cell — that is how an expression that reads the property (`prop(...)`) picks it
 * up. Every other target produces a leaf value nobody reads, so it gets a
 * collision-free synthetic ref derived from the target's own fields.
 */
function targetToVariableRef(target) {
    if (target.kind === "ITEM_PROPERTY_VALUE") {
        return { scope: target.itemId, key: target.propertyKey };
    }
    return {
        scope: NON_PROPERTY_SCOPE,
        key: stableKey(target),
    };
}
function stableKey(value) {
    if (typeof value !== "object" || value === null) {
        return JSON.stringify(value);
    }
    const fields = Object.entries(value)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([key, field]) => `${JSON.stringify(key)}:${stableKey(field)}`);
    return `{${fields.join(",")}}`;
}
function inputVariable(ref, property) {
    return property.value.kind === "missing"
        ? { ref, source: { kind: "missing" } }
        : { ref, source: { kind: "fixed", value: property.value } };
}
/**
 * Evaluate a list of expressions against the supplied properties, in the order
 * the caller supplies them.
 *
 * The expressions MUST already be in dependency (topological) order: an
 * `ITEM_PROPERTY_VALUE` expression *produces* the property
 * `(target.itemId, target.propertyKey)`, and any expression that reads it
 * (`prop(...)`) must be placed after it. `evaluate` neither builds nor consults
 * a dependency graph; it folds each producer's result into the property set for
 * the expressions that follow, so producer/consumer and transitive chains
 * resolve in one call. A consumer placed before its producer resolves to `null`
 * (the producer's value is not yet available). Cycle rejection is the caller's
 * responsibility (`buildExpressionDependencyGraph` / `topologicalOrder`).
 *
 * `input.properties` are item properties, read via `prop(...)`;
 * `input.productProperties` are the properties of the product assigned to an
 * item, read via `productProp(...)` at a scope that cannot collide with the
 * item-property one, so a same-named item property neither shadows nor satisfies
 * a product read.
 *
 * Returns one entry per input expression, positionally aligned. An entry is
 * `null` when the expression could not be computed because an input was missing
 * (absent property or `null` value), or because a producer it depends on yielded
 * `null`; `EXPLICIT_NULL` for the explicit `__NULL__` sentinel; otherwise an
 * `ExpressionResult`. A result whose runtime type does not match its
 * `resultDataType` raises an error rather than yielding `null`.
 */
export function evaluate(expressions, input) {
    // Translate into the engine's variable list: the input properties first, then
    // the computed expressions in the caller-supplied order. A property the caller
    // states to be value-less becomes a *missing* variable at its own
    // `(itemId, key)` ref rather than being omitted, so a read of it consults a
    // fallback; a property the caller omits stays an unresolvable reference. Only an
    // ITEM_PROPERTY_VALUE expression shares an address space with input properties;
    // if such a property is also supplied as input, its producer (resolved later)
    // wins by last-writer. The other targets — including selection setters — get
    // synthetic refs and so never override an input property here.
    //
    // Product properties live at the product scope, which no expression target can
    // address, so they are pure inputs no producer can overwrite.
    const fixedVariables = [
        ...input.properties.map(property => inputVariable({ scope: property.itemId, key: property.key }, property)),
        ...input.productProperties.map(property => inputVariable({ scope: productVariableScope(property.itemId), key: property.key }, property)),
    ];
    const computedVariables = expressions.map(expression => ({
        ref: targetToVariableRef(expression.target),
        source: {
            kind: "computed",
            resultDataType: expression.resultDataType,
            expression: expression.value,
        },
    }));
    const resolved = resolveVariables([...fixedVariables, ...computedVariables]);
    // The computed variables were appended after the fixed ones, in input order,
    // so their results map back to the input expressions positionally.
    return resolved.slice(fixedVariables.length);
}
//# sourceMappingURL=evaluate.js.map